import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import Notifications from '@jll/notifications';
import { withOverflowMenu } from '@jll/overflow-menu';
import styles from './avatar.scss';

const Avatar = ({
    image,
    notifications,
    name,
    className,
    showName,
    size,
    onClick,
    link,
    linkTarget,
    verticalLayout,
    position,
    market,
    award,
    menuItems,
    menuAlign,
    itemClassname
}) => {
    let Element = 'div';
    const getStyles = classNames.bind(styles);
    const componentMainClass = 'avatar';
    const avatarClass = getStyles(
        componentMainClass,
        {
            [`${componentMainClass}--${size}`]: size,
            [`${componentMainClass}--no-border`]: image
        },
        className
    );

    const getInitials = string => {
        const names = string.split(' ');
        let initials = names[0].substring(0, 1).toUpperCase();

        if (names.length > 1) {
            initials += names[names.length - 1].substring(0, 1).toUpperCase();
        }
        return initials;
    };

    const attributes = {
        className: avatarClass
    };

    const overflowMenuProps = {
        content: menuItems,
        className: '',
        closeOnClickOutside: true,
        duration: 50,
        align: menuAlign,
        trigger: 'click',
        size: 'small',
        itemClassname
    };

    const renderAvatar = () => (
        <Element {...attributes}>
            <div tabIndex={-1} role="button" className={styles[`${componentMainClass}__image-holder`]} onClick={menuItems.length === 0 ? onClick : null} onKeyUp={() => {}}>
                {notifications ? <Notifications size={size} className={styles[`${componentMainClass}__badge`]} notifications={notifications} /> : null}
                {image ? <img alt={`${name} avatar`} className={styles[`${componentMainClass}__image`]} src={image} /> : null}
                <div className={styles[`${componentMainClass}__initials`]}>{getInitials(name)}</div>
            </div>
            {showName ? <div className={styles[`${componentMainClass}__name`]}>{name}</div> : null}
        </Element>
    );

    const renderAvatarVertical = () => (
        <div className={styles[`${componentMainClass}__avatar-container`]}>
            <div className={styles[`${componentMainClass}__avatar-wrapper`]}>
                <Element {...attributes}>
                    <div tabIndex={-1} role="button" className={styles[`${componentMainClass}__image-holder`]} onClick={onClick} onKeyUp={() => {}}>
                        {image ? <img alt={`${name} avatar`} className={styles[`${componentMainClass}__image`]} src={image} /> : null}
                        <div className={styles[`${componentMainClass}__initials`]}>{getInitials(name)}</div>
                    </div>
                </Element>
                <div className={award ? styles[`${componentMainClass}__award`] : styles[`${componentMainClass}__award-invisible`]}>{award}</div>
                <div className={styles[`${componentMainClass}__name-vertical`]}>{name}</div>
                {position && <div className={styles[`${componentMainClass}__position-vertical`]}>{position}</div>}
                {market && <div className={styles[`${componentMainClass}__market-vertical`]}>{market}</div>}
            </div>
        </div>
    );

    const ComponentWithOverflowMenu = withOverflowMenu(renderAvatar, overflowMenuProps);

    if (link) {
        Element = 'a';
        attributes.href = link;
        attributes.target = linkTarget;
    }

    if (!verticalLayout) {
        return menuItems.length === 0 ? renderAvatar() : <ComponentWithOverflowMenu />;
    }

    return renderAvatarVertical();
};

Avatar.propTypes = {
    /** The class of the avatar component wrapper */
    className: PropTypes.string,
    /** URL to and user avatar */
    image: PropTypes.string,
    /** Avatar link. On click navigates user somewhere. If there is no link, you can assign onClick action instead. */
    link: PropTypes.string,
    /** The target of the link. Possible target props are identical to <a target=""> tag props. */
    linkTarget: PropTypes.string,
    /** Name of user. Used to generates initials and show name */
    name: PropTypes.string.isRequired,
    /** Shows notifcations count */
    notifications: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.func]),
    /** Callback function fired when the avatar is clicked. */
    onClick: PropTypes.func,
    /** Shows full name besides avatar */
    showName: PropTypes.bool,
    /** Size of avatar */
    size: PropTypes.oneOf(['extralarge', 'large', 'medium', 'small']),
    /** Indicates if the avatar layout is vertical, recommended using only with extralarge size */
    verticalLayout: PropTypes.bool,
    /** User's position in the organization */
    position: PropTypes.string,
    /** Indicates the user's assigned market */
    market: PropTypes.string,
    /** Indicates the award to display */
    award: PropTypes.string,
    /** Items to display in the overflow menu, if item list is passed the click event onf the Avatar will inhibit,
        Items should be constructed in the parent element and come with an onClick handler, doesn't affect vertical layout */
    menuItems: PropTypes.arrayOf(PropTypes.any),
    /** Sets overflow menu's content alignment. Available alignments: center, left, right. */
    menuAlign: PropTypes.oneOf(['center', 'right', 'left', '']),
    /** Classname to set styles of overflow menu. */
    itemClassname: PropTypes.string
};

Avatar.defaultProps = {
    className: '',
    image: '',
    link: '',
    linkTarget: '',
    notifications: null,
    onClick: undefined,
    showName: false,
    size: 'large',
    verticalLayout: false,
    position: '',
    market: '',
    award: '',
    menuItems: [],
    menuAlign: 'left',
    itemClassname: ''
};

export default Avatar;
